import Input from '@/components/Input';
import RequestModal from '@/components/modals/RequestModal';
import Panel from '@/components/panel/Panel';
import { FiPlus, FiSearch, FiStar, TbCircleDashedPlus } from '@/components/react-icons/main';
import IconV2 from '@/components/v2/IconV2';
import fuseSearch from '@/lib/fuse';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import memoizeSelector from '@/redux/lib/memoizeSelector';
import selectors from '@/redux/selectors/selectors';
import { appsMetadata } from '@/redux/selectors/workflowSelectors';
import utilityAppIcons from '@/screens/workflows/schema/utilityAppIcons';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const AppPicker = forwardRef(({ onAppSelect, onKeyPress, searchPlaceholder = 'Search for an app', enableKeyboardShortcuts = true, showRequestModal = true, selectedApps = null, autoFocus = true, excludeAppIds = [], search: externalSearch, onSearchChange, priorityAppIds = [], allowCustomApp = false, maxApps = null, maxVisibleApps = null, searchThreshold = 0.3, selectedCustomApp = null }, ref) => {
  const [internalSearch, setInternalSearch] = useState('');
  const search = externalSearch !== undefined ? externalSearch : internalSearch;
  const setSearch = onSearchChange || setInternalSearch;
  const visibleApps = useSelector(memoizeSelector('visibleApps', (state) => appsMetadata(state)?.filter((n) => !n?.hidden && n?.category === 'app')));
  const [canClear, setCanClear] = useState(false);
  const isLoadingPostAppRequest = useArgSelector(selectors.isLoading, 'postAppRequest');

  const filteredApps = useMemo(() => {
    if (!visibleApps || visibleApps.length === 0) {
      return [];
    }

    const appsToSearch = visibleApps.filter((n) => !excludeAppIds?.includes(n?.id));
    const searchedApps = fuseSearch(appsToSearch, search, ['name'], { threshold: searchThreshold });
    const appsWithIcons = searchedApps.map((n) => ({ ...n, icon: n?.icon || utilityAppIcons[n?.slug] || FiStar }));

    // Pin selected apps first, then priority apps. When the user is searching, preserve the
    // relevance order Fuse returns (best match first); otherwise browse alphabetically.
    // Re-sorting search results alphabetically buries the best match (e.g. "NetSuite") at the
    // bottom of the list, which makes search look broken.
    const hasSearch = Boolean(search && search.trim());
    const prioritySet = new Set(priorityAppIds || []);
    const selectedSet = new Set(selectedApps?.map((app) => app?.id) || []);
    const sorted = appsWithIcons.sort((a, b) => {
      const aIsSelected = selectedSet.has(a.id);
      const bIsSelected = selectedSet.has(b.id);
      if (aIsSelected && !bIsSelected) return -1;
      if (!aIsSelected && bIsSelected) return 1;
      const aIsPriority = prioritySet.has(a.id);
      const bIsPriority = prioritySet.has(b.id);
      if (aIsPriority && !bIsPriority) return -1;
      if (!aIsPriority && bIsPriority) return 1;
      if (hasSearch) return 0;
      return a.name.localeCompare(b.name);
    });
    return maxApps ? sorted.slice(0, maxApps) : sorted;
  }, [visibleApps, search, excludeAppIds, priorityAppIds, selectedApps, maxApps, searchThreshold]);

  const showCustomAppOption = useMemo(() => {
    if (!allowCustomApp || !search) return false;
    return !filteredApps.some((app) => app.name.toLowerCase() === search.toLowerCase());
  }, [allowCustomApp, search, filteredApps]);

  const effectiveMaxVisible = useMemo(() => {
    if (!maxVisibleApps) return null;
    let count = maxVisibleApps;
    if (showCustomAppOption) count--;
    if (selectedCustomApp) count--;
    return count;
  }, [maxVisibleApps, showCustomAppOption, selectedCustomApp]);

  const visibleFilteredApps = useMemo(() => {
    if (!effectiveMaxVisible) return filteredApps;
    return filteredApps.slice(0, effectiveMaxVisible);
  }, [filteredApps, effectiveMaxVisible]);

  const moreCount = useMemo(() => {
    if (!effectiveMaxVisible) return 0;
    return Math.max(0, filteredApps.length - effectiveMaxVisible);
  }, [filteredApps, effectiveMaxVisible]);

  const handleCustomAppClick = () => {
    onAppSelect({ name: search, custom: true });
  };

  const handleKeyDown = (e) => {
    if (enableKeyboardShortcuts && e.key === 'Enter' && filteredApps?.length === 1) {
      onAppSelect(filteredApps[0]);
    }
  };

  const handleAppClick = (app) => {
    onAppSelect(app);
  };

  const handleAppKeyPress = (e, app) => {
    if (e.key === 'Enter') {
      onAppSelect(app);
    }
    if (onKeyPress) {
      onKeyPress(e, app);
    }
  };

  const emptyStateActions = [
    {
      label: 'Request new app',
      className: 'primary',
      icon: TbCircleDashedPlus,
      onClick: () => {
        setModalState('request', { open: true, type: 'app' });
      }
    }
  ];

  const handleOpenRequestModal = () => {
    setModalState('request', { open: true, type: 'app' });
  };

  const handleRequestKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleOpenRequestModal();
    }
  };

  useEffect(() => {
    if (isLoadingPostAppRequest) {
      setCanClear(true);
    } else {
      if (canClear) {
        setSearch('');
        setCanClear(false);
        const input = document.querySelector('.comp-app-picker .app-picker-search input');
        if (input) {
          input.focus();
        }
      }
    }
  }, [isLoadingPostAppRequest]);

  return (
    <div className="comp-app-picker">
      <Input size="lg" ref={ref} className="app-picker-search" autoFocus={autoFocus} icon={FiSearch} placeholder={searchPlaceholder} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={handleKeyDown} />
      <div className="app-picker-grid-container">
        {(filteredApps?.length > 0 || showCustomAppOption || selectedCustomApp) && (
          <div className="app-picker-grid">
            {selectedCustomApp && (
              <div className="app-picker-grid-item custom-app selected" tabIndex={0} onClick={() => onAppSelect({ name: selectedCustomApp.name, custom: true })} onKeyPress={(e) => e.key === 'Enter' && onAppSelect({ name: selectedCustomApp.name, custom: true })} role="button">
                <IconV2 icon={FiPlus} size={'lg'} />
                <div className="app-picker-grid-item-name" data-title={selectedCustomApp.name}>
                  {selectedCustomApp.name}
                </div>
              </div>
            )}
            {visibleFilteredApps?.map((app) => (
              <div className={`app-picker-grid-item ${selectedApps?.some?.((selectedApp) => selectedApp?.id === app?.id) ? 'selected' : ''}`} key={app?.id} tabIndex={0} onClick={() => handleAppClick(app)} onKeyPress={(e) => handleAppKeyPress(e, app)} role="button">
                <IconV2 icon={app?.icon} size={'lg'} />
                <div className="app-picker-grid-item-name" data-title={app?.name}>
                  {app?.name}
                </div>
              </div>
            ))}
            {moreCount > 0 && (
              <div className="app-picker-grid-item more-apps">
                <div className="app-picker-grid-item-name" data-title={`...${moreCount} more`}>
                  ...{moreCount} more
                </div>
              </div>
            )}
            {showCustomAppOption && (
              <div className="app-picker-grid-item custom-app" tabIndex={0} onClick={handleCustomAppClick} onKeyPress={(e) => e.key === 'Enter' && handleCustomAppClick()} role="button">
                <IconV2 icon={FiPlus} size={'lg'} />
                <div className="app-picker-grid-item-name" data-title={`Add "${search}"`}>
                  Add "{search}"
                </div>
              </div>
            )}
            {showRequestModal && (
              <div className="app-picker-grid-item request-app workflow" tabIndex={0} onClick={handleOpenRequestModal} onKeyPress={handleRequestKeyPress} role="button">
                <IconV2 icon={FiSearch} size={'lg'} />
                <div className="app-picker-grid-item-name" data-title={"Can't find your app?"}>
                  {"Can't find your app?"}
                </div>
              </div>
            )}
          </div>
        )}
        {filteredApps?.length === 0 && !showCustomAppOption && !selectedCustomApp && (
          <>
            <Panel.EmptyState title={"Can't find your app?"} description={'Request an app and we will build it for you!'} icon={FiSearch} className="app-picker-empty-state" actions={emptyStateActions} />
          </>
        )}
        {showRequestModal && <RequestModal />}
      </div>
    </div>
  );
});

AppPicker.displayName = 'AppPicker';

export default AppPicker;
